import { useState } from "react";
import { AlertCircle, Headset } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { brand, materials, waLink } from "@/content/site";
import { Reveal, SectionHead, WhatsAppIcon, Ltr, Arrow } from "@/components/apex/primitives";

const Inquiry = () => {
  const { t, isRtl } = useLang();
  const [city, setCity] = useState("");
  const [finish, setFinish] = useState("");
  const [height, setHeight] = useState("");
  const [width, setWidth] = useState("");
  const [project, setProject] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState(false);

  const buildMessage = () => {
    const unit = t.dimensions.meter;
    const lines = [
      isRtl
        ? "مرحبًا APEX، أرغب بمناقشة باب Pivot لمدخلي."
        : "Hello APEX, I would like to discuss a pivot door for my entrance.",
      `${isRtl ? "البلد" : "City"}: ${city.trim()}`,
    ];
    if (finish) lines.push(`${t.inquiry.finish}: ${finish}`);
    if (height.trim()) lines.push(`${t.inquiry.height}: ${height.trim()} ${unit}`);
    if (width.trim()) lines.push(`${t.inquiry.width}: ${width.trim()} ${unit}`);
    if (project) lines.push(`${t.inquiry.project}: ${project}`);
    if (notes.trim()) lines.push(`${t.inquiry.notes}: ${notes.trim()}`);
    return lines.join("\n");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!city.trim()) {
      setErr(true);
      document.getElementById("inq-city")?.focus();
      return;
    }
    setErr(false);
    window.open(waLink(buildMessage()), "_blank", "noopener,noreferrer");
  };

  const Label = ({ htmlFor, children, required }) => (
    <label htmlFor={htmlFor} className="flex items-baseline gap-2 text-[14px] font-semibold text-[var(--text)]">
      {children}
      <span className={`text-[11px] font-medium ${required ? "text-[var(--yellow-dk)]" : "text-[var(--muted-2)]"}`}>
        {required ? t.inquiry.required : t.inquiry.optional}
      </span>
    </label>
  );

  return (
    <section id="inquiry" data-testid="inquiry-section" className="relative bg-[var(--cream-2)] py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <div>
            <SectionHead label={t.inquiry.label} title={t.inquiry.title} body={t.inquiry.body} />
            <Reveal delay={0.12}>
              <div className="ap-dark mt-10 overflow-hidden rounded-[var(--r)] p-7">
                <div className="flex items-center gap-4">
                  <span className="ap-icon">
                    <Headset className="h-6 w-6" strokeWidth={1.7} />
                  </span>
                  <div>
                    <p className="text-[13px] text-white/65">{t.contact.call}</p>
                    <a href={brand.telUrl} data-testid="inquiry-phone" className="ap-display text-2xl text-white hover:text-[var(--yellow)]">
                      <Ltr>{brand.phoneDisplay}</Ltr>
                    </a>
                  </div>
                </div>
                <p className="mt-6 text-[13.5px] leading-relaxed text-white/60">{t.inquiry.noPrice}</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.08}>
            <form onSubmit={onSubmit} noValidate data-testid="inquiry-form" className="grid gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="inq-city" required>
                  {t.inquiry.city}
                </Label>
                <input
                  id="inq-city"
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    if (err) setErr(false);
                  }}
                  placeholder={t.inquiry.cityPh}
                  aria-required="true"
                  aria-invalid={err}
                  aria-describedby={err ? "inq-city-err" : undefined}
                  data-testid="inquiry-city"
                  className={`ap-field mt-2.5 ${err ? "ap-field-err" : ""}`}
                />
                {err && (
                  <p id="inq-city-err" role="alert" data-testid="inquiry-city-error" className="mt-2.5 flex items-center gap-2 text-[13px] font-medium text-[#c9423e]">
                    <AlertCircle className="h-4 w-4" strokeWidth={2} />
                    {t.inquiry.cityErr}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <span className="flex items-baseline gap-2 text-[14px] font-semibold text-[var(--text)]">
                  {t.inquiry.finish}
                  <span className="text-[11px] font-medium text-[var(--muted-2)]">{t.inquiry.optional}</span>
                </span>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {materials.map((m) => {
                    const name = isRtl ? m.name_ar : m.name_en;
                    const on = finish === name;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setFinish(on ? "" : name)}
                        aria-pressed={on}
                        data-testid={`inquiry-finish-${m.id}`}
                        className={`ap-filter ${on ? "ap-filter-on" : ""}`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="inq-h">{t.inquiry.height}</Label>
                <div className="relative mt-2.5">
                  <input
                    id="inq-h"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    data-testid="inquiry-height"
                    className="ap-field pe-12"
                    dir="ltr"
                  />
                  <span className="pointer-events-none absolute inset-y-0 end-4 grid place-items-center text-[13px] font-medium text-[var(--muted)]">
                    {t.dimensions.meter}
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor="inq-w">{t.inquiry.width}</Label>
                <div className="relative mt-2.5">
                  <input
                    id="inq-w"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    data-testid="inquiry-width"
                    className="ap-field pe-12"
                    dir="ltr"
                  />
                  <span className="pointer-events-none absolute inset-y-0 end-4 grid place-items-center text-[13px] font-medium text-[var(--muted)]">
                    {t.dimensions.meter}
                  </span>
                </div>
              </div>

              <div className="sm:col-span-2">
                <span className="flex items-baseline gap-2 text-[14px] font-semibold text-[var(--text)]">
                  {t.inquiry.project}
                  <span className="text-[11px] font-medium text-[var(--muted-2)]">{t.inquiry.optional}</span>
                </span>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {[t.inquiry.projectNew, t.inquiry.projectReplace].map((p, i) => {
                    const on = project === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setProject(on ? "" : p)}
                        aria-pressed={on}
                        data-testid={`inquiry-project-${i}`}
                        className={`ap-filter ${on ? "ap-filter-on" : ""}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="inq-notes">{t.inquiry.notes}</Label>
                <textarea
                  id="inq-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t.inquiry.notesPh}
                  data-testid="inquiry-notes"
                  className="ap-field mt-2.5 h-auto resize-y"
                />
              </div>

              <div className="flex flex-col gap-5 pt-2 sm:col-span-2 sm:flex-row sm:items-center">
                <button type="submit" data-testid="inquiry-submit" className="ap-btn-wa shrink-0">
                  <WhatsAppIcon className="h-5 w-5" />
                  {t.inquiry.submit}
                  <Arrow />
                </button>
                <p className="text-[13px] leading-relaxed text-[var(--muted)]">{t.inquiry.hint}</p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Inquiry;
